import moment from 'moment';
import { mapActions, mapGetters, mapState } from 'vuex';
import accountSocialAvatar from '@/components/account/account-social-avatar/account-social-avatar.vue';

const TOP_RECENT_COMMENTS_SKELETON_COUNT = 3;
const COMMENT_LINK_CLASS = 'sushi-market-top-comments__comment-link';
const COMMENT_MENTION_CLASS = 'sushi-market-top-comments__comment-mention';
const COMMENT_CODE_CLASS = 'sushi-market-top-comments__comment-code';
const COMMENT_QUOTE_CLASS = 'sushi-market-top-comments__comment-quote';
const COMMENT_SPOILER_CLASS = 'sushi-market-top-comments__comment-spoiler';

const createPreviewSpan = (className, textContent) => {
  const span = document.createElement('span');
  span.className = className;
  span.textContent = (textContent || '').trim();
  return span;
};

const replaceNodeWithNode = (node, replacementNode) => {
  if (!node || !node.parentNode || !replacementNode) {
    return;
  }

  node.parentNode.replaceChild(replacementNode, node);
};

const replaceBlockquoteWithPreviewQuote = (blockquote) => {
  if (!blockquote || !blockquote.parentNode) {
    return;
  }

  const fragment = document.createDocumentFragment();
  const quoteText = (blockquote.textContent || '').trim();

  if (blockquote.previousSibling) {
    fragment.appendChild(document.createElement('br'));
  }

  fragment.appendChild(createPreviewSpan(COMMENT_QUOTE_CLASS, quoteText));
  fragment.appendChild(document.createElement('br'));
  blockquote.parentNode.replaceChild(fragment, blockquote);
};

const normalizeMentionText = (textContent) => {
  const text = (textContent || '').trim();

  if (!text) {
    return '';
  }

  return text.charAt(0) === '@' ? text : `@${text}`;
};

const normalizeCommentPreviewHtml = (commentHtml) => {
  if (!commentHtml || typeof document === 'undefined') {
    return commentHtml || '';
  }

  const container = document.createElement('div');
  container.innerHTML = commentHtml;

  // Normalize the preview into inert markup so the widget behaves as a single click target.
  Array.from(container.querySelectorAll('span[spoiler]')).forEach((spoiler) => {
    spoiler.removeAttribute('spoiler');
    spoiler.classList.add(COMMENT_SPOILER_CLASS);
  });

  Array.from(container.querySelectorAll('a[mention], a[data-dsq-mention]')).forEach((mentionLink) => {
    replaceNodeWithNode(
      mentionLink,
      createPreviewSpan(COMMENT_MENTION_CLASS, normalizeMentionText(mentionLink.textContent)),
    );
  });

  Array.from(container.querySelectorAll('a')).forEach((anchor) => {
    const linkText = (anchor.textContent || anchor.getAttribute('href') || '').trim();
    replaceNodeWithNode(anchor, createPreviewSpan(COMMENT_LINK_CLASS, linkText));
  });

  Array.from(container.querySelectorAll('pre, code, kbd')).forEach((codeElement) => {
    replaceNodeWithNode(codeElement, createPreviewSpan(COMMENT_CODE_CLASS, codeElement.textContent));
  });

  Array.from(container.querySelectorAll('blockquote')).forEach(replaceBlockquoteWithPreviewQuote);

  return container.innerHTML;
};

export default {
  components: {
    accountSocialAvatar,
  },
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    hasDesktopRightRail: {
      type: Boolean,
      default: false,
    },
    market: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('app', ['isCommentEnabled']),
    ...mapState('market', ['marketTopComments', 'marketTopCommentsLoaded', 'marketTopCommentsLoading', 'marketTopCommentsMarketId']),
    marketId() {
      return this.market && this.market.marketId ? this.market.marketId : null;
    },
    activeMarketId() {
      return this.isActive ? this.marketId : null;
    },
    topComments() {
      return this.marketTopComments || {
        title: 'Top Recent Comments',
        comments: [],
      };
    },
    comments() {
      return this.topComments.comments || [];
    },
    previewComments() {
      return this.comments.map(comment => ({
        ...comment,
        previewHtml: normalizeCommentPreviewHtml(comment.commentHtml),
      }));
    },
    isLoadedForMarket() {
      return this.marketTopCommentsLoaded && this.marketTopCommentsMarketId === this.marketId;
    },
    showTopComments() {
      return this.isActive &&
        this.isCommentEnabled &&
        this.isLoadedForMarket &&
        this.comments.length > 0;
    },
    showSkeleton() {
      return this.isActive &&
        this.isCommentEnabled &&
        this.marketTopCommentsLoading &&
        !this.isLoadedForMarket;
    },
    rootModifiers() {
      return {
        'sushi-market-top-comments--with-right-rail': this.hasDesktopRightRail,
      };
    },
    skeletonCount() {
      return TOP_RECENT_COMMENTS_SKELETON_COUNT;
    },
  },
  methods: {
    ...mapActions('market', ['getMarketTopComments']),
    loadTopComments(marketId) {
      if (!marketId) {
        return Promise.resolve();
      }

      return this.getMarketTopComments(marketId);
    },
    formatDate(value) {
      if (!value) {
        return '';
      }

      const date = moment.utc(value, moment.ISO_8601, true);
      const now = moment.utc();

      if (!date.isValid()) {
        return '';
      }

      const secondsDiff = now.diff(date, 'seconds');

      if (secondsDiff < 0) {
        return date.local().toDate().toLocaleDateString('en-US');
      }

      if (secondsDiff < 60) {
        const elapsed = Math.max(secondsDiff, 1);
        return elapsed === 1 ? '1 second ago' : `${elapsed} seconds ago`;
      }

      const minutesDiff = now.diff(date, 'minutes');

      if (minutesDiff < 60) {
        return minutesDiff === 1 ? '1 minute ago' : `${minutesDiff} minutes ago`;
      }

      const hoursDiff = now.diff(date, 'hours');

      if (hoursDiff < 24) {
        return hoursDiff === 1 ? '1 hour ago' : `${hoursDiff} hours ago`;
      }

      if (hoursDiff < 48) {
        return 'Yesterday';
      }

      return date.local().toDate().toLocaleDateString('en-US');
    },
    onOpenDiscussion() {
      this.$emit('open-discussion');
    },
    onCommentClick(event, comment) {
      if (event) {
        event.preventDefault();
      }

      this.onOpenComment(comment);
    },
    onOpenComment(comment) {
      this.$emit('open-comment', comment);
    },
  },
  watch: {
    activeMarketId: {
      handler: 'loadTopComments',
      immediate: true,
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/page-market-detail-sushi/pages/sushi-market-contracts/components/sushi-market-top-comments/sushi-market-top-comments.js